import React, { useState, useEffect } from "react";
import SEO from "../seo";
const ViewManageYourBill = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["overview", "view-bill", "invoice-details", "faq"];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="View & Manage Your Bill – xPage Help Center"
        description="Learn how to view, download, and manage your xPage invoices. Step-by-step instructions and FAQ included."
        keywords="xPage, view bill, invoice, billing history, help center"
        canonical="https://help.xpage.ai/help/understand-xpage/View%20%26%20Manage%20Your%20Bill"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title */}
        <h1 className="text-systemTitle text-2xl font-semibold mb-3">
          How to View Your xPage Bill
        </h1>

        {/* Introduction Paragraph */}
        <p className="text-sm text-systemLabel mb-6">
          You can view, download, or print your xPage invoices directly from
          your admin.
        </p>

        {/* Overview Section */}
        <section id="overview" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Overview
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              You can view, download, or print your xPage invoices directly from
              your admin.
            </p>
          </div>
        </section>

        {/* View Your Bill Section */}
        <section id="view-bill" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            View Your Bill
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps:
            </h3>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to your xPage account.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Go to Settings then Billing.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Under Billing History, click on the invoice you want to view.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Download or print the invoice for your records.
                </span>
              </li>
            </ol>
          </div>
        </section>

        {/* Invoice Details Section */}
        <section id="invoice-details" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Invoice Details
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">Each invoice shows:</p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>Invoice number</li>
              <li>Billing period</li>
              <li>Description of charges</li>
              <li>Subtotal, taxes, and total due</li>
              <li>Payment method used</li>
            </ul>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            xPage Billing FAQ
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-systemTitle mb-1">
                  Q: When will I be billed?
                </h4>
                <p className="text-sm text-systemLabel ml-4">
                  A: Subscription fees are billed at the start of your billing
                  cycle. Transaction fees are billed when your threshold is
                  reached or at the end of your cycle, whichever comes first.
                </p>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-systemTitle mb-1">
                  Q: What is a threshold payment?
                </h4>
                <p className="text-sm text-systemLabel ml-4">
                  A: A threshold is a set amount of accumulated transaction fees
                  that triggers a bill. It starts at $15 and can increase as you
                  build payment history.
                </p>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-systemTitle mb-1">
                  Q: Can I change my billing frequency?
                </h4>
                <p className="text-sm text-systemLabel ml-4">
                  A: Yes. You can switch between monthly and annual billing from
                  your admin.
                </p>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-systemTitle mb-1">
                  Q: Are there any hidden fees?
                </h4>
                <p className="text-sm text-systemLabel ml-4">
                  A: No. All fees are listed in your plan details and your bill.
                </p>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-systemTitle mb-1">
                  Q: What happens if my payment fails?
                </h4>
                <p className="text-sm text-systemLabel ml-4">
                  A: We'll retry up to three times in 72 hours. If unsuccessful,
                  checkout will be paused until payment is resolved.
                </p>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-systemTitle mb-1">
                  Q: How do I get a copy of my invoice?
                </h4>
                <p className="text-sm text-systemLabel ml-4">
                  A: Go to Settings then Billing then Billing History, select
                  the invoice, and download it.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#view-bill"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "view-bill"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                View Your Bill
              </a>
            </li>
            <li>
              <a
                href="#invoice-details"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "invoice-details"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Invoice Details
              </a>
            </li>
            <li>
              <a
                href="#faq"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "faq"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                FAQ
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ViewManageYourBill;
